import {addMonths} from 'date-fns';
import {
  Button,
  ButtonGroup,
  Card,
  closeDialog,
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Grid, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useGetVehicleV2Query, useUpdateVehicleV2Mutation} from '@dms/api/sales';
import {VehicleTypeEnumObject} from '@dms/api/shared';
import {useGetServiceCheckInQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, serviceIntervalsDefault} from '@dms/shared';

import {getApiDateString, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ServiceType} from '../types/ServiceType';
import {UpdateServiceIntervalForm} from '../types/UpdateServiceIntervalForm';
import {fluidUpdate} from '../utils/fluidUpdate';
import {replacementUpdate} from '../utils/replacementUpdate';

interface UpdateDialogFormProps extends RequiredTestIdProps {
  dialogId: string;
  vehicleId: string;
  serviceCaseId: string;
}

export function UpdateDialogForm(props: UpdateDialogFormProps) {
  const vehicleV2Query = useGetVehicleV2Query({vehicleId: props.vehicleId});
  const checkinQuery = useGetServiceCheckInQuery({serviceCaseId: props.serviceCaseId});

  const isLoading = vehicleV2Query.isLoading || checkinQuery.isLoading;
  const isError = vehicleV2Query.isError || checkinQuery.isError;

  const [updateVehicle] = useUpdateVehicleV2Mutation();

  const getDefaultValues = (): UpdateServiceIntervalForm => ({
    date: getApiDateString(new Date()),
    mileage: checkinQuery.data?.mileage ?? null,
    options: [],
  });

  const handleSubmit: FormSubmitHandler<UpdateServiceIntervalForm> = (formData) => {
    if (!vehicleV2Query.data) {
      return Promise.reject();
    }

    const vehicleDataCopy = structuredClone(vehicleV2Query.data);

    const serviceIntervals = vehicleDataCopy.serviceIntervals ?? serviceIntervalsDefault;

    formData.options.forEach((option) => {
      match(option)
        .with(ServiceType.ServiceInspection, () => {
          serviceIntervals.serviceInspection.lastServiceInspectionDate = formData.date;
          serviceIntervals.serviceInspection.lastServiceInspectionMileage = formData.mileage ?? 0;

          if (serviceIntervals.serviceInspection.replacementAfterMonths) {
            serviceIntervals.serviceInspection.nextServiceInspectionDate = getApiDateString(
              addMonths(
                serviceIntervals.serviceInspection.lastServiceInspectionDate,
                serviceIntervals.serviceInspection.replacementAfterMonths
              )
            );
          }

          if (serviceIntervals.serviceInspection.replacementAfterMileage) {
            serviceIntervals.serviceInspection.nextServiceInspectionMileage =
              serviceIntervals.serviceInspection.lastServiceInspectionMileage +
              serviceIntervals.serviceInspection.replacementAfterMileage;
          }
        })
        .with(ServiceType.EngineOil, () => {
          serviceIntervals.engineOil = replacementUpdate(serviceIntervals.engineOil, formData);
        })
        .with(ServiceType.GearboxOil, () => {
          serviceIntervals.gearboxOil = replacementUpdate(serviceIntervals.gearboxOil, formData);
        })
        .with(ServiceType.FourByFourOilDistributor, () => {
          serviceIntervals.fourByFourOilDistributor = replacementUpdate(
            serviceIntervals.fourByFourOilDistributor,
            formData
          );
        })
        .with(ServiceType.TimingBelt, () => {
          serviceIntervals.timingBelt = replacementUpdate(serviceIntervals.timingBelt, formData);
        })
        .with(ServiceType.BrakeFluid, () => {
          serviceIntervals.brakeFluid = fluidUpdate(serviceIntervals.brakeFluid, formData);
        })
        .with(ServiceType.CoolantFluid, () => {
          serviceIntervals.coolantFluid = fluidUpdate(serviceIntervals.coolantFluid, formData);
        })
        .exhaustive();
    });

    const {dimensions: _, ...rest} = vehicleDataCopy;

    const updatedVehicleData = {
      ...rest,
      serviceIntervals,
      make: rest.make || '',
      modelFamily: rest.modelFamily || '',
      type: rest.type || VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
    };

    return updateVehicle(updatedVehicleData)
      .unwrap()
      .then(() => {
        closeDialog(props.dialogId);
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form
        defaultValues={getDefaultValues()}
        experimentalZodSchema={schema}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <Text size="small">{i18n.t('entity.vehicle.labels.updateServiceIntervalsNote')}</Text>
            <Card variant="inlineGrey">
              <VStack>
                <Grid columns={2}>
                  <FormField
                    label={i18n.t('general.labels.date')}
                    type="apiDate"
                    name="date"
                    control={control}
                    isRequired
                    data-testid={suffixTestId('date', props)}
                  />
                  <FormField
                    label={i18n.t('entity.vehicle.labels.mileage')}
                    type="number"
                    name="mileage"
                    control={control}
                    suffix={i18n.t('general.metric.km')}
                    isRequired
                    data-testid={suffixTestId('mileage', props)}
                  />
                </Grid>
                <Separator />
                <VStack>
                  <FormField
                    type="checkboxes"
                    name="options"
                    control={control}
                    direction="vertical"
                    options={[
                      {
                        label: i18n.t('entity.vehicle.labels.serviceInspection'),
                        value: ServiceType.ServiceInspection,
                      },
                      {
                        label: i18n.t('entity.vehicle.labels.engineOil'),
                        value: ServiceType.EngineOil,
                      },
                      {
                        label: i18n.t('entity.vehicle.labels.gearboxOil'),
                        value: ServiceType.GearboxOil,
                      },
                      {
                        label: i18n.t('entity.vehicle.labels.fourByFourOilDistributor'),
                        value: ServiceType.FourByFourOilDistributor,
                      },
                      {
                        label: i18n.t('entity.vehicle.labels.timingBelt'),
                        value: ServiceType.TimingBelt,
                      },
                      {
                        label: i18n.t('entity.vehicle.labels.brakeFluid'),
                        value: ServiceType.BrakeFluid,
                      },
                      {
                        label: i18n.t('entity.vehicle.labels.coolantFluid'),
                        value: ServiceType.CoolantFluid,
                      },
                    ]}
                    data-testid={suffixTestId('options', props)}
                  />
                </VStack>
              </VStack>
            </Card>
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.labels.discard')}
                variant="secondary"
                isDisabled={formApi.formState.isSubmitting}
                onClick={() => closeDialog(props.dialogId)}
                data-testid={suffixTestId('discard', props)}
              />
              <Button
                title={i18n.t('general.actions.update')}
                variant="primary"
                type="submit"
                isLoading={formApi.formState.isSubmitting}
                isDisabled={!formApi.formState.isValid}
                data-testid={suffixTestId('update', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

const schema = z.object({
  date: z.string().min(1),
  mileage: z.number(),
  options: z.array(z.nativeEnum(ServiceType)).min(1),
});
